/**
 * Walk-mode ground lock (parent spec §9.1, PRD R5, AC-N1).
 *
 * Walk mode is interactive feedback, not the LOS truth surface (hard
 * constraint #1: the analytical DEM raster is the only source of LOS
 * truth; mesh raycasts are interactive feedback only). Here "the terrain"
 * for ground-lock purposes is whatever the caller's `sampleGround` reports
 * — in the running viewer that is the LOD1 TIN collider (R5), which is
 * exactly what the navigation spec asks walk mode to be constrained to.
 *
 * This module is deliberately engine-agnostic (no three.js import) so it
 * can be unit-tested in plain Node against synthetic or real ground
 * samples without a renderer or DOM.
 */

export const EYE_HEIGHT_M = 1.6;

/**
 * Fly/orbit terrain-floor clearance (issue #50), metres. Distinct from
 * EYE_HEIGHT_M (the walk-mode eye height): fly and orbit are free-flight
 * modes, not a walking eye, so this only needs to be enough that the near
 * plane doesn't clip through the surface — not a realistic eye height. It is
 * a pure safety net: with the orbit pivot seated on the ground (cameraRig
 * seatOrbitTargetIfNeeded), normal tilting keeps the camera above terrain, so
 * this only engages on a genuine sub-terrain excursion (flying down, or
 * orbiting hard against a hill).
 */
export const MIN_FLY_CLEARANCE_M = 2;

export interface WalkPose {
  x: number;
  /** eye elevation, metres AOD */
  y: number;
  z: number;
}

/**
 * Ground sampler: given a horizontal viewer-space position, return the
 * terrain elevation (metres AOD) at that point, or null if no terrain data
 * is loaded there (e.g. outside all currently-loaded tiles).
 */
export type GroundSampler = (x: number, z: number) => number | null;

/**
 * Advance a walk-mode pose by a horizontal displacement (dx, dz) and
 * re-lock the eye height to groundHeight + EYE_HEIGHT_M.
 *
 * If the sampler reports no ground at the destination (e.g. walked off
 * the edge of loaded tiles), the horizontal move is rejected and the pose
 * is returned unchanged — this is the terrain-collider behaviour required
 * by §9.1 ("terrain collider prevents passing through hills" extends to
 * "never leaves the camera off the collider with an undefined height").
 */
export function walkStep(pose: WalkPose, dx: number, dz: number, sampleGround: GroundSampler): WalkPose {
  const nx = pose.x + dx;
  const nz = pose.z + dz;
  const ground = sampleGround(nx, nz);
  if (ground === null) {
    return pose;
  }
  return { x: nx, z: nz, y: ground + EYE_HEIGHT_M };
}

/** Jump straight to a horizontal position and lock the eye height there. */
export function lockToGround(x: number, z: number, sampleGround: GroundSampler): WalkPose | null {
  const ground = sampleGround(x, z);
  if (ground === null) return null;
  return { x, y: ground + EYE_HEIGHT_M, z };
}

/**
 * Terrain floor for fly and orbit modes (issue #50): raises `pose.y` to at
 * least `sampleGround(pose.x, pose.z) + minClearanceM` so free flight (or an
 * orbit swing against a hill) cannot dip the camera under the surface. Only
 * ever lifts, never lowers.
 *
 * Mirrors walkStep's no-ground convention — if the sampler has no data at
 * this position (outside all loaded tiles), the pose is returned unchanged:
 * never trap or snap the camera to a fabricated height (hard constraint #7).
 * Also returns the *same object reference* when the pose is already at or
 * above the floor, so the common per-frame case allocates nothing; callers
 * can identity-compare the result to detect "no change".
 */
export function clampAboveGround(pose: WalkPose, sampleGround: GroundSampler, minClearanceM: number): WalkPose {
  const ground = sampleGround(pose.x, pose.z);
  if (ground === null) return pose;
  const floor = ground + minClearanceM;
  if (pose.y >= floor) return pose;
  return { x: pose.x, y: floor, z: pose.z };
}
